import { getActivityReport, getActivityStats, getStudentActivityReport } from '@/app/services/reports/reports';
import * as Actions from '@/app/store/actions';
import { Avatar } from '@material-ui/core';
import { Check } from '@material-ui/icons';
import dayjs from 'dayjs';
import { default as moment, default as momentTime } from 'moment-timezone';
import Pusher from 'pusher-js';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import secureLocalStorage from 'react-secure-storage';
import { useReactToPrint } from 'react-to-print';
import MediaRenderer from '../old reports/MediaRenderer';
import AppliedFiltersBar from '@shared/Reports/AppliedFiltersBar';
import ReportsHeader from '@shared/Reports/ReportsHeader';
import StatBox from '@shared/Reports/StatBox';
import TableTooltip from '@shared/Reports/TableTooltip';
import ActivityFiltersBar from './ActivityFiltersBar';
import ActivityPrintViewTable from './ActivityPrintTable';
import ActivityTable from './ActivityTable';
import ActivityViewModal from './ActivityViewModal';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const ActivityReports = () => {
	const navigate = useNavigate();
	const [filters, setFilters] = useState({
		start_date: null,
		end_date: null,
		room_id: [],
		student_id: [],
		status: { label: 'Active', value: true },
		timezone: null,
		sort: 'date',
		dir: 'desc',
		page: 1,
		activity_id: [],
		media_type: null,
	});
	const user = useSelector((state) => state.auth.user);
	const viewAsId = secureLocalStorage.getItem('view_as_id');
	const authToken = secureLocalStorage.getItem('jwt_access_token');
	const [loading, setLoading] = useState(false);
	const [printData, setPrintData] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [printDataLoading, setPrintDataLoading] = useState(false);
	const printRef = useRef();
	const [statsLoading, setStatsLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [totalResults, setTotalResults] = useState(0);
	const [activeStudent, setActiveStudent] = useState(null);
	const [studentActivity, setStudentActivity] = useState(null);
	const dispatch = useDispatch();
	const cache = useRef({}); // Cache object to prevent refetching pages already fetched
	const prevFilters = useRef(filters); // Track previous filters to know when to refetch a set of results
	const [statsData, setStatsData] = useState({});
	const [exportType, setExportType] = useState(null);
	const [exportLoading, setExportLoading] = useState(false);
	const [activityModalOpen, setActivityModalOpen] = useState(false);
	const [activityModalData, setActivityModalData] = useState({
		student: null,
		studentActivity: null,
		loader: false,
		date: null,
	});
	const exportTimeoutRef = useRef(null);
	const pdfRef = useRef(null);
	useEffect(() => {
		const pusher = new Pusher(import.meta.env.VITE_PUSHER_CHANNEL_ID, {
			cluster: import.meta.env.VITE_PUSHER_CLUSTER_ID,
			channelAuthorization: {
				headers: {
					Authorization: `Bearer ${authToken}`,
				},
				endpoint: `${import.meta.env.VITE_API_ENDPOINT}broadcasting/auth`,
			},
		});
		const channel = pusher.subscribe(
			`private-App.Models.User.${user.role[0] === 'super_school_admin' ? viewAsId : user.data?.id}`
		);

		channel.bind('report.export.complete', (res) => {
			const exportLink = JSON.parse(window.localStorage.getItem('export_uuid'));
			if (exportLink && exportLink === res.uuid) {
				setExportLoading(false);
				if (exportTimeoutRef.current) {
					clearTimeout(exportTimeoutRef.current);
				}
				window.localStorage.removeItem('export_uuid');

				const anchor = document.createElement('a');
				anchor.href = res.link;
				anchor.download = res.filename;
				anchor.click();
				anchor.remove();
			}
		});

		return () => {
			pusher.disconnect();
		};
	}, []);

	const handlePrint = useReactToPrint({
		content: () => printRef.current,
	});

	useEffect(() => {
		setFilters({
			...filters,
			timezone: user.data.school.timezone,
		});
	}, [user]);

	const calculatePercentChange = (current, previous) => {
		if (previous === 0) {
			return current * 100;
		}
		return (((current - previous) / previous) * 100).toFixed(1);
	};

	const generateQueryParams = (filters, exportType = null) => {
		return {
			page: filters.page,
			start_date: filters.start_date,
			end_date: filters.end_date,
			status: filters.status?.value === true ? 1 : filters.status?.value === false ? 0 : null,
			timezone: filters.timezone,
			export: exportType,
			sort: filters.sort,
			dir: filters.dir,
			student_id: filters.student_id?.length > 0 ? filters.student_id.map((item) => item.value) : null,
			room_id: filters.room_id?.length > 0 ? filters.room_id.map((item) => item.value) : null,
			activity_id: filters.activity_id?.length > 0 ? filters.activity_id.map((item) => item.value) : null,
			media_type: [filters?.media_type?.value],
		};
	};

	const timeZone = momentTime.tz.guess();

	const getStudentActivity = (row) => {
		getStudentActivityReport(row.student_id, row.activity_date, timeZone)
			.then((response) => {
				setActivityModalData({
					student: row,
					studentActivity: response?.data,
					loader: false,
					date: row.activity_date,
				});
				setActivityModalOpen(true);
			})
			.catch((error) => {
				dispatch(Actions.showMessage({ message: error.message }));
			});
	};

	const openViewActivity = (student) => {
		setActiveStudent(student);

		getStudentActivity(student);
	};

	// Helper function to update filters and reset page to 1 if any filter other than page changes
	const updateFilters = (newFilters) => {
		const filtersChanged = Object.keys(newFilters).some((key) => key !== 'page' && newFilters[key] !== filters[key]);
		if (filtersChanged) {
			setFilters({ ...newFilters, page: 1 });
		} else {
			setFilters(newFilters);
		}
	};

	useEffect(() => {
		// Clear cache if any filter other than page changes
		const filtersChanged = Object.keys(filters).some(
			(key) => key !== 'page' && filters[key] !== prevFilters.current[key]
		);
		if (filtersChanged) {
			cache.current = {};
		}
		prevFilters.current = filters;

		if (filters.start_date && filters.end_date) {
			setLoading(true);
			const queryParams = generateQueryParams(filters);
			const queryKey = JSON.stringify(queryParams);

			// Check if the results for the current page are already in the cache
			if (cache.current[queryKey]) {
				const cachedData = cache.current[queryKey];
				setRows(cachedData.data);
				setTotalResults(cachedData.total);
				setLoading(false);

				// Fetch stats data only when filters other than page change and total results > 0
				if (filtersChanged && cachedData.total > 0) {
					fetchStatsData(queryParams);
				}
			} else {
				getActivityReport(queryParams)
					.then((res) => {
						setStudentActivity(res.data.data);
						setRows(res.data.data);
						setTotalResults(res.data.meta.total);
						// Store the results in the cache
						cache.current[queryKey] = {
							data: res.data.data,
							total: res.data.meta.total,
						};

						// Fetch stats data only when filters other than page change and total results > 0
						if (filtersChanged && res.data.meta.total > 0) {
							fetchStatsData(queryParams);
						} else if (filtersChanged) {
							setStatsData({});
						}
					})
					.catch(() => {
						dispatch(
							Actions.showMessage({
								message: 'Failed to fetch report',
								variant: 'error',
							})
						);
						setRows([]);
						setTotalResults(0);
						setStatsData({});
					})
					.finally(() => {
						setLoading(false);
					});
			}
		} else {
			// Clear data if start_date and end_date is not set
			setRows([]);
			setTotalResults(0);
			setStatsData({});
		}
	}, [filters]);

	const fetchStatsData = async (queryParams) => {
		setStatsLoading(true);
		try {
			const res = await getActivityStats(queryParams);
			const stats = res.data;

			const formatDateRange = (start, end) => {
				const startDate = moment(start);
				const endDate = moment(end);
				if (startDate.month() === endDate.month()) {
					return `${startDate.format('MMM D')} - ${endDate.format('D')}`;
				} else {
					return `${startDate.format('MMM D')} - ${endDate.format('MMM D')}`;
				}
			};

			setStatsData({
				total_activities: {
					total: stats.average_activities,
					percentChange: calculatePercentChange(stats.average_activities, stats.previous.average_activities),
					dateRange: formatDateRange(stats.previous.start_date, stats.previous.end_date),
				},
				average_with_photo: {
					total: stats.average_with_photo,
					percentChange: calculatePercentChange(stats.average_with_photo, stats.previous.average_with_photo),
					dateRange: formatDateRange(stats.previous.start_date, stats.previous.end_date),
				},
				average_with_video: {
					total: stats.average_with_video,
					percentChange: calculatePercentChange(stats.average_with_video, stats.previous.average_with_video),
					dateRange: formatDateRange(stats.previous.start_date, stats.previous.end_date),
				},
			});
		} catch {
			dispatch(
				Actions.showMessage({
					message: 'Failed to fetch statistics data',
					variant: 'error',
				})
			);
			setStatsData({});
		} finally {
			setStatsLoading(false);
		}
	};

	const handleExport = async (type) => {
		if (filters.start_date && filters.end_date) {
			const queryParams = generateQueryParams(filters, type);
			try {
				setExportLoading(true);
				queryParams.uuid = uuidv4();
				window.localStorage.setItem('export_uuid', JSON.stringify(queryParams.uuid));
				await getActivityReport(queryParams);

				// Set a timeout to reset exportLoading if no response within 20 seconds
				exportTimeoutRef.current = setTimeout(() => {
					if (!exportLoading) return;
					setExportLoading(false);
					dispatch(
						Actions.showMessage({
							message: 'Export failed to generate. Please try again.',
							variant: 'error',
						})
					);
				}, 20000);
			} catch {
				dispatch(
					Actions.showMessage({
						message: 'Failed to export report',
						variant: 'error',
					})
				);
			}
		}
	};

	const handlePdfExport = async () => {
		// Calculate difference between start_date and end_date
		const diffDays = moment(filters.end_date).diff(moment(filters.start_date), 'days');
		if (diffDays > 7) {
			dispatch(
				Actions.showMessage({
					message:
						'PDF export over 7 days is not supported. Select to Print and Print to PDF for date ranges over 7 days',
					variant: 'warning',
				})
			);
			return;
		}
		return handleExport('pdf');
	};

	const handlePrintClick = async () => {
		setExportType('print');
		if (filters.start_date && filters.end_date) {
			setPrintDataLoading(true);
			try {
				const queryParams = generateQueryParams(filters, 'print');

				const res = await getActivityReport(queryParams);
				setPrintData(res.data.data);
			} catch (error) {
				console.error('Error fetching all pages for print:', error);
			} finally {
				setPrintDataLoading(false);
			}
		}
	};

	useEffect(() => {
		if (!printDataLoading && printData.length > 0) {
			handlePrint();
		}
	}, [printDataLoading, printData]);

	const columns = [
		{
			id: 'date',
			label: 'Date',
			render: (row) => (
				<button
					className="hover:underline cursor-pointer"
					onClick={() =>
						setFilters({
							...filters,
							page: 1,
							start_date: moment(row.activity_date).format('YYYY-MM-DD'),
							end_date: moment(row.activity_date).format('YYYY-MM-DD'),
						})
					}
					type="button"
				>
					{moment(row.activity_date).format('MM/DD/YY')}
				</button>
			),
			printRender: (row) => moment(row.activity_date).format('MM/DD/YY'),
			widthClass: '',
		},
		{
			id: 'student',
			label: 'Student',
			render: (row) => {
				return (
					<div className="flex items-center gap-16">
						<Avatar src={row?.thumb} />
						<TableTooltip>
							<button
								className="hover:underline cursor-pointer font-bold"
								onClick={() => {
									setFilters({
										...filters,
										page: 1,
										student_id: [
											{
												label: `${row.last_name}, ${row.first_name}`,
												value: row.student_id,
											},
										],
									});
								}}
								type="button"
							>
								{`${row.last_name}, ${row.first_name}`}
							</button>
						</TableTooltip>
					</div>
				);
			},
			printRender: (row) => {
				return `${row.last_name}, ${row.first_name}`;
			},
			widthClass: 'min-w-256',
		},
		{
			id: 'room',
			label: 'Room',
			render: (row) => (
				<div className="flex items-center gap-16">
					<Avatar src={row.room.thumb} />
					<button
						className="hover:underline cursor-pointer font-bold"
						onClick={() =>
							setFilters({
								...filters,
								page: 1,
								room_id: [{ label: row.room.name, value: row.room.id }],
							})
						}
						type="button"
					>
						{row.room.name}
					</button>
				</div>
			),
			printRender: (row) => row.room.name,
			widthClass: 'min-w-200',
		},
		{
			id: 'activity',
			widthClass: 'min-w-200',
			label: 'Activity',
			render: (row) => {
				return (
					<div className="flex items-center">
						{row.activity.slice(0, 5).map((activity) => (
							<div className="flex items-center gap-0 -ml-12 border-white border-2 rounded-full" key={activity.id}>
								<Avatar className="border-3 border-white" src={activity.thumb} />
							</div>
						))}
						{row.activity.length > 5 ? (
							<TableTooltip
								title={
									<div className="text-pd-sm">
										{row.activity.slice(5).map((activity) => (
											<div key={activity.id}> {activity.name}</div>
										))}
									</div>
								}
							>
								<div className="flex items-center gap-0 -ml-12 border-white border-2 rounded-full">
									<Avatar className="border-3 bg-pd-blue-light border-white text-pd-sm">
										+{row.activity.length - 5}
									</Avatar>
								</div>
							</TableTooltip>
						) : null}
					</div>
				);
			},
			printRender: (row) => <div>{row.activity.map((activity) => activity.name).join(', ')}</div>,
		},
		{
			id: 'photos',
			label: 'Photos',
			render: (row) => row.num_photos,
			printRender: (row) => row.num_photos,
			widthClass: 'max-w-96',
		},
		{
			id: 'videos',
			label: 'Videos',
			widthClass: 'max-w-96',
			render: (row) => row.num_videos,
			printRender: (row) => row.num_videos,
		},
		{
			id: 'activity_report',
			label: 'Activity Report',
			widthClass: 'min-w-100',
			render: (row) => {
				return (
					<button className="text-pd-blue-light font-bold" onClick={() => openViewActivity(row)} type="button">
						View
					</button>
				);
			},
			printRender: () => null,
		},
	];

	return (
		<div>
			{activityModalOpen ? (
				<ActivityViewModal
					date={activityModalData.date}
					loader={activityModalData.loader}
					open={activityModalOpen}
					pdfRef={pdfRef}
					setOpen={setActivityModalOpen}
					student={activityModalData.student}
					studentActivity={activityModalData.studentActivity}
				/>
			) : null}
			<ReportsHeader
				exportLoading={exportLoading}
				exportsDisabled={!rows?.length}
				onBack={() => navigate(-1)}
				onDownloadPDF={handlePdfExport}
				onExport={handleExport}
				onPrint={handlePrintClick}
				pdfLoading={false}
				printLoading={printDataLoading}
				subtitle="Track activities for students across your programs, in individual rooms, or multiple rooms."
				title="Activity Report"
			/>
			<ActivityFiltersBar filters={filters} setFilters={updateFilters} />
			<AppliedFiltersBar filters={filters} setFilters={updateFilters} />
			<div className="bg-white">
				{Object.keys(statsData).length > 0 ? (
					<div className="px-32 py-48">
						<div className="flex flex-col sm:flex-row sm:justify-between gap-16 md:gap-36 sm:max-w-4xl mx-auto">
							<StatBox
								data={statsData.total_activities}
								loading={statsLoading || loading}
								title="Avg. Number of Activities Posted"
								type="totalStudent"
							/>
							<StatBox
								data={statsData.average_with_photo}
								loading={statsLoading || loading}
								title="Avg. Number of Posts with Photos"
								type="dailyStudent"
							/>
							<StatBox
								data={statsData.average_with_video}
								loading={statsLoading || loading}
								title="Avg. Number of Posts with Videos"
								type="dailyTime"
							/>
						</div>
					</div>
				) : null}
				<div>
					<ActivityTable
						columns={columns}
						data={rows}
						dir={filters.dir}
						filters={filters}
						loading={loading}
						page={filters.page}
						rowsPerPage={rowsPerPage}
						setDir={(dir) => updateFilters({ ...filters, dir })}
						setFilters={updateFilters}
						setPage={(page) => updateFilters({ ...filters, page })}
						setRowsPerPage={setRowsPerPage}
						setSort={(sort) => updateFilters({ ...filters, sort })}
						sort={filters.sort}
						totalResults={totalResults}
					/>
				</div>
				<div className="h-0 overflow-hidden">
					<ActivityPrintViewTable
						columns={columns}
						data={printData}
						exportType={exportType}
						filters={filters}
						ref={printRef}
						title="Activity Report"
						user={user}
					/>
				</div>
			</div>
			{!!activeStudent && !!activityModalData?.studentActivity ? (
				<div className="h-0 overflow-hidden justify-between bg-white w-full mt-32">
					<div
						ref={pdfRef}
						style={{
							width: '100%',
							backgroundColor: 'white',
							// display: 'none'
						}}
					>
						<div className="bg-white p-16" style={{ backgroundColor: '#5b81f0', height: 96 }}>
							<div
								className="flex flex-col"
								style={{
									gap: 'auto',
									height: 105,
								}}
							>
								<div className="flex items-center flex-nowrap justify-between">
									<span className="text-pd-xl self-end font-bold mr-28">
										<div className="report-std truncate " style={{ display: 'flex', alignItems: 'center' }}>
											<Avatar src={activeStudent.thumb} style={{ width: '40px', height: '40px' }} />
											<span style={{ marginLeft: '10px', color: '#fff' }}>
												{activeStudent.first_name} {activeStudent.last_name}
											</span>
										</div>
									</span>
									<div className="flex justify-between">
										<div className="flex">
											<div className="flex">
												<div className="flex flex-col items-end" style={{ color: '#fff', marginRight: '22px' }}>
													<div>Daily Report</div>
													<div> {dayjs(filters.date).format('MMMM DD, YYYY')}</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div>
							<div className="flex flex-col-reverse mt-10">
								{studentActivity.length === 0 ? (
									<div className="flex justify-center items-center h-full">
										<div className="flex flex-col items-center">No Activity for today</div>
									</div>
								) : null}
								{activityModalData.studentActivity?.map((obj) => {
									return (
										(obj.type === 'checkin' && (
											<div className="box-padding">
												<div className="text-pd-xl self-end font-bold mr-28 my-4">
													<div className="flex">
														<div className="checkin-tick-icon">
															<Check color="white" />
														</div>
														<div className="flex flex-col items-start" style={{ marginLeft: '14px' }}>
															<div style={{ fontSize: '13px', paddingBottom: '4px' }}>
																Checked in at {dayjs(`${obj.data.checkin_date}Z`).format('hh:mm A')}
															</div>
														</div>
													</div>
												</div>
											</div>
										)) ||
										(obj.type === 'checkout' && (
											<div className="box-padding">
												<div className="text-pd-xl self-end font-bold mr-28">
													<div className="flex border-b pb-10">
														<div className="checkin-tick-icon">
															<Check color="white" />
														</div>
														<div className="flex flex-col items-start" style={{ marginLeft: '14px' }}>
															<div style={{ fontSize: '13px', paddingBottom: '4px' }}>
																Checked out at {dayjs(`${obj.data.checkout_date}Z`).format('hh:mm A')}
															</div>
														</div>
													</div>
												</div>
											</div>
										)) ||
										(obj.type === 'activities' &&
											obj.data?.map((activity) => {
												return (
													<div className="box-padding" key={activity.id}>
														<div className="text-pd-xl self-end font-bold mr-28">
															<div className="flex border-b pb-10">
																<Avatar src={activity.activity.thumb} />
																<div className="flex flex-col items-start" style={{ marginLeft: '14px' }}>
																	<div
																		style={{
																			fontSize: '13px',
																			paddingBottom: '4px',
																		}}
																	>
																		{activity.activity.name} at {dayjs(activity.created_at).format('hh:mm A')}
																	</div>
																	<div
																		style={{
																			fontSize: '13px',
																			paddingBottom: '4px',
																			color: '#676767',
																		}}
																	>
																		{activity.activity_id !== 9 ? activity.activity.name : null}
																		{activity.activity_id === 9 ? activity.activities.name : null}
																		{activity?.activities?.types && activity.activity_id !== 12
																			? ` - ${activity?.activities?.types}`
																			: ''}
																		{activity.package ? ` - ${activity.package}` : ''}
																		{activity.activity_id !== 9 && activity?.start_time
																			? ` - ${activity?.start_time.split(' ').slice(1, 3).join(' ')}`
																			: ''}
																		{activity.activity_id !== 9 && activity?.end_time
																			? ` to ${activity?.end_time.split(' ').slice(1, 3).join(' ')}`
																			: ''}
																		{activity?.activities?.bullet_comments
																			? ` - ${activity?.activities?.bullet_comments}`
																			: ''}
																		{activity?.activities?.quick_comments
																			? ` - ${activity?.activities?.quick_comments}`
																			: ''}
																		{activity.activity_id === 10 && activity.activity_supplies.length > 0
																			? `: ${activity?.activity_supplies
																					?.map((supply) => supply.supply_name)
																					.join(' - ')}`
																			: ''}
																		{activity.other_comment ? ` - ${activity.other_comment}` : ''}
																	</div>
																	{activity.photo || activity.media.length > 0 || activity.video ? (
																		<MediaRenderer
																			media={activity.media}
																			photo={activity.photo}
																			video={activity.video}
																			videoThumbnail={activity.video_thumb}
																		/>
																	) : null}
																</div>
															</div>
														</div>
													</div>
												);
											}))
									);
								})}
							</div>
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default ActivityReports;
